/**
 * Upgraded Settlement Rate Card Component
 * Displays settlement percentage doughnut with detailed status breakdown.
 * @module components/dashboard/settlement-rate-card
 */

import { Percent, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { t } from "@/lib/i18n/messages";
import { computeSettlementRate } from "@/lib/utils/aggregate";
import type { Locale } from "@/types/auth";
import type { Transaction } from "@/types/wallet";
import { cn } from "@/lib/utils";

ChartJS.register(ArcElement, Tooltip, Legend);

interface SettlementRateCardProps {
  locale: Locale;
  transactions: Transaction[];
  loading: boolean;
  className?: string;
}

export function SettlementRateCard({
  locale,
  transactions,
  loading,
  className,
}: SettlementRateCardProps) {
  const isRTL = locale === "ar";

  if (loading) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs space-y-4 h-full",
          className
        )}
        aria-hidden="true"
      >
        <Skeleton className="h-6 w-36 rounded-lg" />
        <div className="flex items-center justify-center py-6">
          <Skeleton className="h-44 w-44 rounded-full" />
        </div>
        <div className="space-y-2 pt-2">
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const stats = computeSettlementRate(transactions);

  if (stats.total === 0) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs h-full flex flex-col justify-between",
          className
        )}
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
            <Percent className="w-4 h-4" />
          </span>
          <h3 className="text-lg font-bold text-foreground">
            {t(locale, "settlementRate")}
          </h3>
        </div>
        <EmptyState
          icon={Percent}
          title={t(locale, "noData")}
          description={t(locale, "noDataAvailable")}
          className="border-0 p-4"
        />
      </div>
    );
  }

  const chartData = {
    labels: [
      t(locale, "settled"),
      t(locale, "pending"),
      t(locale, "failed"),
    ],
    datasets: [
      {
        data: [stats.settledCount, stats.pendingCount, stats.failedCount],
        backgroundColor: ["#10B981", "#F59E0B", "#EF4444"],
        borderColor: "transparent",
        hoverOffset: 4,
      },
    ],
  };

  const chartOptions = {
    cutout: "75%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        rtl: isRTL,
        callbacks: {
          label: (ctx: { dataset: { data: number[] }; dataIndex: number; label: string }) => {
            const count = ctx.dataset.data[ctx.dataIndex] ?? 0;
            const pct = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : "0";
            return ` ${ctx.label}: ${count} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs flex flex-col justify-between h-full space-y-6",
        className
      )}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Percent className="w-4 h-4" />
            </span>
            <h3 className="text-base md:text-lg font-bold text-foreground">
              {isRTL ? "نسبة التسوية المالية" : "Settlement Rate"}
            </h3>
          </div>
          <p className="text-xs text-foreground-secondary mt-1">
            {isRTL
              ? "اكتمال تسوية العمليات المالية في المحفظة."
              : "Financial settlement and transaction completion status."}
          </p>
        </div>

        {/* Badge */}
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
          {stats.settledPercent}% {isRTL ? "مكتمل" : "Settled"}
        </span>
      </div>

      {/* ─── Doughnut Chart with Center Metric ───────────────────────── */}
      <div className="relative flex items-center justify-center my-2">
        <div className="w-44 h-44 sm:w-48 sm:h-48 relative flex items-center justify-center">
          <Doughnut data={chartData} options={chartOptions} />

          {/* Center Overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
            <span className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
              {stats.settledPercent}%
            </span>
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-0.5">
              {isRTL ? "عمليات مكتملة" : "Settled"}
            </span>
            <span className="text-[10px] text-foreground-secondary mt-0.5">
              {stats.total} {isRTL ? "إجمالي العمليات" : "total txs"}
            </span>
          </div>
        </div>
      </div>

      {/* ─── Status Breakdown Rows ───────────────────────────────────── */}
      <div className="space-y-2.5 pt-1">
        {/* Settled */}
        <div className="flex items-center justify-between p-2.5 rounded-xl bg-background/60 border border-border text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              {t(locale, "settled")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground">{stats.settledCount}</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              ({stats.total > 0 ? ((stats.settledCount / stats.total) * 100).toFixed(0) : 0}%)
            </span>
          </div>
        </div>

        {/* Pending */}
        <div className="flex items-center justify-between p-2.5 rounded-xl bg-background/60 border border-border text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-500" />
              {t(locale, "pending")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground">{stats.pendingCount}</span>
            <span className="text-amber-600 dark:text-amber-400 font-medium">
              ({stats.total > 0 ? ((stats.pendingCount / stats.total) * 100).toFixed(0) : 0}%)
            </span>
          </div>
        </div>

        {/* Failed */}
        {stats.failedCount > 0 && (
          <div className="flex items-center justify-between p-2.5 rounded-xl bg-background/60 border border-border text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="font-semibold text-foreground flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                {t(locale, "failed")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">{stats.failedCount}</span>
              <span className="text-red-600 dark:text-red-400 font-medium">
                ({stats.total > 0 ? ((stats.failedCount / stats.total) * 100).toFixed(0) : 0}%)
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
